import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Settings, User, Trash2, Key, UserPlus } from 'lucide-react';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [userToChangePassword, setUserToChangePassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [fromProfessionalDialogOpen, setFromProfessionalDialogOpen] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState('');
  const [newUserFromProf, setNewUserFromProf] = useState({
    username: '',
    password: '',
    role: 'educador'
  });
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'educador',
    is_admin: false,
    permissions: {}
  });

  const availablePermissions = [
    { key: 'children_view', label: 'Visualizar Crianças' },
    { key: 'children_edit', label: 'Editar Crianças' },
    { key: 'professionals_view', label: 'Visualizar Profissionais' },
    { key: 'professionals_edit', label: 'Editar Profissionais' },
    { key: 'activities_view', label: 'Visualizar Atividades' },
    { key: 'activities_edit', label: 'Editar Atividades' },
    { key: 'groups_view', label: 'Visualizar Grupos/Turmas' },
    { key: 'groups_edit', label: 'Editar Grupos/Turmas' },
    { key: 'enrollments_view', label: 'Visualizar Matrículas' },
    { key: 'enrollments_edit', label: 'Editar Matrículas' },
    { key: 'attendance_view', label: 'Visualizar Chamada' },
    { key: 'attendance_edit', label: 'Editar Chamada' },
    { key: 'events_view', label: 'Visualizar Agenda' },
    { key: 'events_edit', label: 'Editar Agenda/Eventos' },
    { key: 'reports_view', label: 'Visualizar Relatórios' },
    { key: 'reports_export', label: 'Exportar Relatórios (PDF/CSV)' },
    { key: 'users_manage', label: 'Gerenciar Usuários' }
  ];

  useEffect(() => {
    fetchUsers();
    fetchProfessionals();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfessionals = async () => {
    try {
      const response = await axios.get(`${API}/professionals`);
      setProfessionals(response.data || []);
    } catch (error) {
      console.log('Erro ao carregar profissionais');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, {
          full_name: formData.full_name,
          email: formData.email,
          role: formData.role,
          is_admin: formData.is_admin,
          permissions: formData.permissions
        });
        toast.success('Usuário atualizado com sucesso!');
      } else {
        await axios.post(`${API}/auth/register`, formData);
        toast.success('Usuário cadastrado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      full_name: user.full_name,
      role: user.role,
      is_admin: user.is_admin || false,
      permissions: user.permissions || {}
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      full_name: '',
      role: 'educador',
      is_admin: false,
      permissions: {}
    });
    setEditingUser(null);
  };

  const togglePermission = (key) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key]
      }
    }));
  };

  // Delete user
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success('Usuário excluído com sucesso!');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  // Change password
  const handleChangePasswordClick = (user) => {
    setUserToChangePassword(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleConfirmChangePassword = async () => {
    if (!userToChangePassword || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    try {
      await axios.put(`${API}/users/${userToChangePassword.id}/change-password`, {
        new_password: newPassword
      });
      toast.success('Senha alterada com sucesso!');
      setPasswordDialogOpen(false);
      setUserToChangePassword(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    }
  };

  // Create user from professional
  const handleOpenFromProfessional = () => {
    setSelectedProfessional('');
    setNewUserFromProf({ username: '', password: '', role: 'educador' });
    setFromProfessionalDialogOpen(true);
  };

  const handleProfessionalSelect = (profId) => {
    setSelectedProfessional(profId);
    const prof = professionals.find(p => p.id === profId);
    if (prof) {
      // Sugerir username baseado no nome
      const suggestedUsername = prof.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .split(' ')[0];
      setNewUserFromProf(prev => ({
        ...prev,
        username: suggestedUsername
      }));
    }
  };

  const handleCreateFromProfessional = async () => {
    if (!selectedProfessional || !newUserFromProf.username || !newUserFromProf.password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      await axios.post(`${API}/users/from-professional`, {
        professional_id: selectedProfessional,
        username: newUserFromProf.username,
        password: newUserFromProf.password,
        role: newUserFromProf.role
      });
      toast.success('Usuário criado com sucesso!');
      setFromProfessionalDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  // Filter professionals that don't have users yet
  const availableProfessionals = professionals.filter(prof => {
    // Check if professional already has a user (by email or professional_id)
    return !users.some(user => 
      user.email === prof.email || 
      user.professional_id === prof.id
    );
  });

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Usuários</h1>
          <p className="text-slate-600 mt-1">Gerencie usuários e permissões</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Button to create from professional */}
          <Button 
            variant="outline" 
            onClick={handleOpenFromProfessional}
            data-testid="create-from-professional-button"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Criar de Profissional
          </Button>

          {/* Button to create new user */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button">
                <Plus className="h-4 w-4 mr-2" />
                Novo Usuário
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="username">Usuário *</Label>
                    <Input
                      id="username"
                      data-testid="user-username-input"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      required
                      disabled={!!editingUser}
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="user-email-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="full_name">Nome Completo *</Label>
                    <Input
                      id="full_name"
                      data-testid="user-fullname-input"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="role">Função *</Label>
                    <select
                      id="role"
                      data-testid="user-role-select"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    >
                      <option value="coordenador">Coordenador</option>
                      <option value="assistente_social">Assistente Social</option>
                      <option value="psicologa">Psicóloga</option>
                      <option value="educador">Educador Social</option>
                    </select>
                  </div>
                  {!editingUser && (
                    <div className="md:col-span-2">
                      <Label htmlFor="password">Senha *</Label>
                      <Input
                        id="password"
                        type="password"
                        data-testid="user-password-input"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required={!editingUser}
                      />
                    </div>
                  )}
                </div>

                {/* Admin Total Checkbox */}
                <div className="p-4 border-2 border-amber-300 bg-amber-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="is_admin"
                      data-testid="is-admin-checkbox"
                      checked={formData.is_admin}
                      onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                      className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500"
                    />
                    <div>
                      <Label htmlFor="is_admin" className="text-base font-semibold text-amber-800 cursor-pointer">
                        Administrador Total
                      </Label>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Se marcado, o usuário terá acesso TOTAL ao sistema, ignorando as permissões abaixo.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Permissões Específicas</Label>
                    {formData.is_admin && (
                      <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded">
                        Admin Total ignora estas permissões
                      </span>
                    )}
                  </div>
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 max-h-64 overflow-y-auto border rounded-lg p-4 ${formData.is_admin ? 'border-slate-200 bg-slate-50 opacity-60' : 'border-slate-200'}`}>
                    {availablePermissions.map(perm => (
                      <div key={perm.key} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={perm.key}
                          data-testid={`permission-${perm.key}`}
                          checked={formData.permissions[perm.key] || false}
                          onChange={() => togglePermission(perm.key)}
                          disabled={formData.is_admin}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50"
                        />
                        <Label htmlFor={perm.key} className={`text-sm font-normal cursor-pointer ${formData.is_admin ? 'text-slate-400' : ''}`}>
                          {perm.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="save-user-button">
                    {editingUser ? 'Atualizar' : 'Cadastrar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="users-list">
          {users.map((user) => (
            <div
              key={user.id}
              className={`bg-white rounded-xl shadow-md p-6 border card-hover ${
                ['admin', 'maycon'].includes(user.username?.toLowerCase()?.trim()) ? 'border-purple-300 ring-2 ring-purple-100' :
                user.is_admin ? 'border-amber-300 ring-2 ring-amber-100' : 'border-slate-100'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    ['admin', 'maycon'].includes(user.username?.toLowerCase()?.trim()) ? 'bg-purple-100' :
                    user.is_admin ? 'bg-amber-100' : 'bg-blue-100'
                  }`}>
                    <User className={`h-6 w-6 ${
                      ['admin', 'maycon'].includes(user.username?.toLowerCase()?.trim()) ? 'text-purple-600' :
                      user.is_admin ? 'text-amber-600' : 'text-blue-600'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{user.full_name || user.username}</h3>
                    <p className="text-xs text-slate-500">{user.role}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  {['admin', 'maycon'].includes(user.username?.toLowerCase()?.trim()) && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                      SUPERADMIN
                    </span>
                  )}
                  {user.is_admin && !['admin', 'maycon'].includes(user.username?.toLowerCase()?.trim()) && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                      ADMIN
                    </span>
                  )}
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-slate-600">Usuário:</span>
                  <p className="font-medium text-slate-800">{user.username}</p>
                </div>
                <div>
                  <span className="text-slate-600">Email:</span>
                  <p className="font-medium text-slate-800 text-xs">{user.email}</p>
                </div>
                <div>
                  <span className="text-slate-600">Permissões:</span>
                  <p className="text-xs text-slate-500">
                    {['admin', 'maycon'].includes(user.username?.toLowerCase()?.trim()) ? (
                      <span className="text-purple-600 font-medium">Superadmin (Fixo)</span>
                    ) : user.is_admin ? (
                      <span className="text-amber-600 font-medium">Acesso Total</span>
                    ) : (
                      `${Object.values(user.permissions || {}).filter(Boolean).length} ativas`
                    )}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleEdit(user)}
                  data-testid={`edit-user-${user.id}`}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangePasswordClick(user)}
                  title="Trocar Senha"
                  data-testid={`change-password-${user.id}`}
                >
                  <Key className="h-4 w-4" />
                </Button>
                {!['admin', 'maycon'].includes(user.username?.toLowerCase()?.trim()) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDeleteClick(user)}
                    title="Excluir Usuário"
                    data-testid={`delete-user-${user.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && users.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center border border-slate-100">
          <Settings className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Nenhum usuário cadastrado</p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Usuário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o usuário <strong>{userToDelete?.full_name || userToDelete?.username}</strong>?
              <br /><br />
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-500" />
              Trocar Senha
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Definir nova senha para: <strong>{userToChangePassword?.full_name || userToChangePassword?.username}</strong>
            </p>
            <div>
              <Label htmlFor="new-password">Nova Senha *</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleConfirmChangePassword}>
                Alterar Senha
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User from Professional Dialog */}
      <Dialog open={fromProfessionalDialogOpen} onOpenChange={setFromProfessionalDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-500" />
              Criar Usuário de Profissional
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Selecione um profissional para criar uma conta de usuário com os dados dele.
            </p>
            
            <div>
              <Label htmlFor="select-professional">Profissional *</Label>
              <select
                id="select-professional"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                value={selectedProfessional}
                onChange={(e) => handleProfessionalSelect(e.target.value)}
              >
                <option value="">Selecione um profissional</option>
                {availableProfessionals.map(prof => (
                  <option key={prof.id} value={prof.id}>
                    {prof.name} ({prof.role || 'Sem função'})
                  </option>
                ))}
              </select>
              {availableProfessionals.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Todos os profissionais já possuem usuário ou não há profissionais cadastrados.
                </p>
              )}
            </div>

            {selectedProfessional && (
              <>
                <div>
                  <Label htmlFor="new-username">Nome de Usuário *</Label>
                  <Input
                    id="new-username"
                    value={newUserFromProf.username}
                    onChange={(e) => setNewUserFromProf(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="ex: maria.silva"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="new-user-password">Senha *</Label>
                  <Input
                    id="new-user-password"
                    type="password"
                    value={newUserFromProf.password}
                    onChange={(e) => setNewUserFromProf(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Mínimo 6 caracteres"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="new-user-role">Função *</Label>
                  <select
                    id="new-user-role"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                    value={newUserFromProf.role}
                    onChange={(e) => setNewUserFromProf(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value="coordenador">Coordenador</option>
                    <option value="assistente_social">Assistente Social</option>
                    <option value="psicologa">Psicóloga</option>
                    <option value="educador">Educador Social</option>
                  </select>
                </div>
              </>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setFromProfessionalDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateFromProfessional}
                disabled={!selectedProfessional || !newUserFromProf.username || !newUserFromProf.password}
                className="bg-green-600 hover:bg-green-700"
              >
                Criar Usuário
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
